import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  deleteSessionsById,
  postSessionsBackoffice,
  type UserSession,
} from 'api/generated'
import { setCurrentUser, unsetCurrentUser } from 'helpers/authentication'

interface AuthState {
  loading: boolean
  error: string | null
  user: UserSession | null
}

const initialState: AuthState = {
  loading: false,
  error: null,
  user: null,
}

export const loginAsync = createAsyncThunk<
  UserSession,
  { email: string; password: string },
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const { user } = await unwrap(postSessionsBackoffice({ body: credentials }))
    if (!user) return rejectWithValue('Login fallito.')

    setCurrentUser({ ...user, user_id: user.id })
    window.location.href = '/'
    return user
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Login fallito.'))
  }
})

export const logoutAsync = createAsyncThunk<
  null,
  string,
  { rejectValue: string }
>('auth/logout', async (token, { rejectWithValue }) => {
  try {
    await unwrap(deleteSessionsById({ path: { id: token } }))
    unsetCurrentUser()
    window.location.href = '/accedi'
    return null
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Logout fallito.'))
  }
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Login failed.'
      })
      .addCase(logoutAsync.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.loading = false
        state.user = null
      })
      .addCase(logoutAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Logout failed.'
      })
  },
})

export const authSliceReducer = authSlice.reducer
